/**
 * 图片空间去重：内容哈希优先，其次按规范化文件名（主文件名区分大小写，扩展名忽略大小写）。
 */

import { imageSpaceAPI } from './api';

export function normalizeImageSpaceName(name) {
  return String(name || '').trim().replace(/\.[^/.]+$/i, '');
}

/** 规范化图片 URL 路径，用于比对是否为同一张已落盘图片 */
export function normalizeImageSpaceUrl(url) {
  if (!url) return '';
  let s = String(url).trim().replace(/\\/g, '/').split('?')[0];
  if (!s) return '';
  const uploadsIdx = s.indexOf('/uploads/');
  if (uploadsIdx >= 0) return s.slice(uploadsIdx);
  if (s.startsWith('uploads/')) return `/${s}`;
  return s;
}

export function isActiveImageSpaceItem(item) {
  return Boolean(item?.id) && !item?.deletedAt;
}

/**
 * 在已有图片空间中查找重复项（未删除）。
 * @param {Array} existingImages
 * @param {{ contentHash?: string, fileName?: string, url?: string, excludeId?: string }} criteria
 */
export function findImageSpaceDuplicate(existingImages, { contentHash, fileName, url, excludeId } = {}) {
  const active = (existingImages || []).filter(isActiveImageSpaceItem);
  const excludeKey = excludeId != null && excludeId !== '' ? String(excludeId) : '';

  if (contentHash) {
    const byHash = active.find(
      (img) => String(img.id) !== excludeKey
        && img.contentHash
        && String(img.contentHash) === String(contentHash),
    );
    if (byHash) return byHash;
  }

  const normalizedUrl = normalizeImageSpaceUrl(url);
  if (normalizedUrl) {
    const byUrl = active.find(
      (img) => String(img.id) !== excludeKey
        && normalizeImageSpaceUrl(img.url || img.src || img.imageUrl) === normalizedUrl,
    );
    if (byUrl) return byUrl;
  }

  const normalizedName = normalizeImageSpaceName(fileName);
  if (!normalizedName) return null;

  return active.find(
    (img) => String(img.id) !== excludeKey
      && normalizeImageSpaceName(img.name) === normalizedName,
  ) || null;
}

/**
 * 过滤待写入图片空间的条目，跳过与已有记录或本批次内重复的图片。
 * @returns {{ accepted: Array, skipped: Array<{ item: object, duplicate?: object, reason: string }> }}
 */
export function filterNewImageSpaceItems(items, existingImages = []) {
  const accepted = [];
  const skipped = [];
  const seenBatchHashes = new Set();
  const seenBatchNames = new Set();
  const seenBatchUrls = new Set();

  for (const item of items || []) {
    if (!item || typeof item !== 'object') continue;

    const itemId = item.id != null ? String(item.id) : '';
    const contentHash = item.contentHash ? String(item.contentHash) : '';
    const fileName = item.name || '';
    const normalizedName = normalizeImageSpaceName(fileName);
    const itemUrl = item.url || item.src || item.imageUrl || '';

    const duplicate = findImageSpaceDuplicate(existingImages, {
      contentHash,
      fileName,
      url: itemUrl,
      excludeId: itemId,
    });
    if (duplicate) {
      skipped.push({ item, duplicate, reason: 'existing' });
      continue;
    }

    if (contentHash && seenBatchHashes.has(contentHash)) {
      skipped.push({ item, reason: 'batch-hash' });
      continue;
    }
    const normalizedUrl = normalizeImageSpaceUrl(itemUrl);
    if (normalizedUrl && seenBatchUrls.has(normalizedUrl)) {
      skipped.push({ item, reason: 'batch-url' });
      continue;
    }
    if (normalizedName && seenBatchNames.has(normalizedName)) {
      skipped.push({ item, reason: 'batch-name' });
      continue;
    }

    if (contentHash) seenBatchHashes.add(contentHash);
    if (normalizedUrl) seenBatchUrls.add(normalizedUrl);
    if (normalizedName) seenBatchNames.add(normalizedName);
    accepted.push(item);
  }

  return { accepted, skipped };
}

function mergeImageSpaceRecords(localImages = [], serverImages = []) {
  const merged = new Map();
  [...localImages, ...serverImages].forEach((img) => {
    if (img?.id) merged.set(String(img.id), img);
  });
  return [...merged.values()];
}

/** 会话内缓存：避免批量上传每张图都拉 pageSize=5000 */
const DEDUPE_CACHE_TTL_MS = 60_000;
/** @type {{ at: number, images: Array|null, inflight: Promise<Array>|null }} */
const dedupeCache = {
  at: 0,
  images: null,
  inflight: null,
};

export function invalidateImageSpaceDedupeCache() {
  dedupeCache.at = 0;
  dedupeCache.images = null;
  dedupeCache.inflight = null;
}

/** 入库成功后写入缓存，避免下一张再全量拉取 */
export function appendImageSpaceDedupeCache(images = []) {
  const list = Array.isArray(images) ? images.filter((img) => img?.id) : [];
  if (list.length === 0) return;
  const base = Array.isArray(dedupeCache.images) ? dedupeCache.images : [];
  dedupeCache.images = mergeImageSpaceRecords(base, list);
  dedupeCache.at = Date.now();
}

/**
 * 合并本地列表与服务端最新列表，供去重比对（优先服务端字段）。
 */
export async function loadExistingImagesForDedupe(localImages = []) {
  const now = Date.now();
  const cacheFresh = Array.isArray(dedupeCache.images)
    && (now - dedupeCache.at) < DEDUPE_CACHE_TTL_MS;

  if (cacheFresh) {
    return mergeImageSpaceRecords(localImages, dedupeCache.images);
  }

  if (!dedupeCache.inflight) {
    // B3：禁止单次 pageSize=5000；分页拉取并硬顶总条数
    const DEDUPE_PAGE_SIZE = 200;
    const DEDUPE_MAX_PAGES = 5;
    dedupeCache.inflight = (async () => {
      const all = [];
      for (let page = 1; page <= DEDUPE_MAX_PAGES; page += 1) {
        const res = await imageSpaceAPI.getAll({ page, pageSize: DEDUPE_PAGE_SIZE });
        const serverList = Array.isArray(res?.images) ? res.images : [];
        all.push(...serverList);
        if (!res?.hasMore || serverList.length < DEDUPE_PAGE_SIZE) break;
      }
      return all;
    })()
      .then((serverList) => {
        dedupeCache.images = serverList;
        dedupeCache.at = Date.now();
        return serverList;
      })
      .catch((error) => {
        console.warn('[图片空间] 拉取服务端列表用于去重失败，仅使用本地列表', error);
        return Array.isArray(dedupeCache.images) ? dedupeCache.images : [];
      })
      .finally(() => {
        dedupeCache.inflight = null;
      });
  }

  try {
    const serverList = await dedupeCache.inflight;
    return mergeImageSpaceRecords(localImages, serverList);
  } catch (error) {
    console.warn('[图片空间] 拉取服务端列表用于去重失败，仅使用本地列表', error);
    return Array.isArray(localImages) ? localImages : [];
  }
}
